import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Alert,
  Paper,
  Button,
} from "@mui/material";
import PaymentDialog from "../../components/Payment/PaymentDialog";
import { Attendee } from "../../redux/types/types";
import {
  getAttendeeApi,
  getPendingEnrollmentsForAttendeeApi,
} from "../../utils/api/attendees";

const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intl = useIntl();
  
  // Get admin and instructor status from Redux
  const { isAdmin, isInstructor } = useSelector((state: RootState) => ({
    isAdmin: state.auth.isAdmin,
    isInstructor: state.auth.isInstructor,
  }));

  // Get parameters from URL
  const attendeeId = searchParams.get("attendee");
  const classId = searchParams.get("classId");
  const enrollmentStatus = searchParams.get("enrollment");

  // Local state
  const [attendee, setAttendee] = useState<Attendee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [classIds, setClassIds] = useState<number[]>([]);

  // Check if this is a pending enrollment (from either direct enrollment or waiting list)
  const isPendingEnrollment =
    enrollmentStatus === "pending" || enrollmentStatus === "waiting";

  // Redirect admin or instructor users
  useEffect(() => {
    if (isAdmin) {
      navigate("/dashboard");
      return;
    }
    if (isInstructor) {
      window.location.href = "/";
      return;
    }
  }, [isAdmin, isInstructor, navigate]);

  useEffect(() => {
    if (!attendeeId || !isPendingEnrollment) {
      setError(intl.formatMessage({ id: "payment.enrollmentError" }));
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch attendee information
        const attendeeResponse = await getAttendeeApi(parseInt(attendeeId!));
        console.log("Attendee data received:", attendeeResponse.data);
        setAttendee(attendeeResponse.data);

        // Use classId from URL parameter if available, otherwise fetch pending enrollments
        if (classId) {
          setClassIds([parseInt(classId)]);
          console.log("Using class ID from URL:", classId);
        } else if (isPendingEnrollment) {
          try {
            const pendingEnrollmentsResponse =
              await getPendingEnrollmentsForAttendeeApi(parseInt(attendeeId!));
            const enrollments = pendingEnrollmentsResponse.data;
            const pendingClassIds = enrollments.map(
              (enrollment: any) => enrollment.classId
            );
            setClassIds(pendingClassIds);
            console.log("Pending enrollment class IDs:", pendingClassIds);
          } catch (error) {
            console.error("Failed to fetch pending enrollments:", error);
            // Continue without class filtering if this fails
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(intl.formatMessage({ id: "payment.enrollmentError" }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [attendeeId, classId, isPendingEnrollment, intl]);

  const handlePaymentSuccess = () => {
    // Redirect to success page or dashboard
    navigate("/dashboard?enrollment=success");
  };

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
          <Box mt={2}>
            <Button onClick={() => navigate("/dashboard")} variant="contained">
              {intl.formatMessage({ id: "common.backToDashboard" })}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show no attendee error
  if (!attendee) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="warning">
            {intl.formatMessage({ id: "payment.enrollmentError" })}
          </Alert>
          <Box mt={2}>
            <Button onClick={() => navigate("/dashboard")} variant="contained">
              {intl.formatMessage({ id: "common.backToDashboard" })}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4}>
        {isPendingEnrollment && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              {intl.formatMessage({ id: "payment.pendingEnrollment" })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {intl.formatMessage({
                id: "payment.pendingEnrollmentDescription",
              })}
            </Typography>
          </Paper>
        )}

        {/* Payment Dialog */}
        {attendee && (
          <PaymentDialog
            open={!isPendingEnrollment || paymentDialogOpen}
            onClose={() => {
              if (isPendingEnrollment) {
                navigate("/dashboard");
              } else {
                setPaymentDialogOpen(false);
              }
            }}
            onPaymentCreated={handlePaymentSuccess}
            attendee={attendee}
            classIds={classIds.length > 0 ? classIds : undefined}
          />
        )}

        {isPendingEnrollment && !paymentDialogOpen && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              onClick={() => setPaymentDialogOpen(true)}
              variant="contained"
              color="primary"
              size="large"
            >
              {intl.formatMessage({ id: "payment.proceedToPayment" })}
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default PaymentPage;
